import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  aiImageLabelToQuality,
  aiImageModelToLabel,
  aiImageQualityToLabel,
  deleteAiImageTaskApi,
  getAiImageTaskApi,
  getAiImageTemplateApi,
  parseAiImageTags,
  previewAiImagePointApi,
  type AiImageTask,
  type AiImageTemplate,
} from "../../service/aiImage";
import { downloadFileSmart } from "../../utils/batchVideoDownload";
import { AppToast } from "../../components/common/AlertMessage";
import "./AiImageDetail.scss";

type Key = "model" | "ratio" | "resolution" | "quality" | "count";

type DetailItem = {
  title: string;
  image: string;
  description: string;
  tags: string[];
  prompt: string;
  params: Record<Key, string>;
  createdAt?: string;
  /** 任务生成时使用的参考图 URL；为空时不渲染参考图片区 */
  referenceImageUrl?: string | null;
};

const emptyItem: DetailItem = {
  title: "",
  image: "",
  description: "",
  tags: [],
  prompt: "",
  params: { model: "", ratio: "", resolution: "", quality: "", count: "" },
};

const labels: Record<Key, string> = {
  model: "模型引擎",
  ratio: "画面比例",
  resolution: "分辨率",
  quality: "画质模式",
  count: "生成数量",
};

const toParams = (source: Partial<AiImageTemplate & AiImageTask>): Record<Key, string> => ({
  ratio: source.aspectRatio || "2:3",
  resolution: source.resolution || "2K",
  // 存后端字段值（LOW/HIGH/EXTREME），UI 显示时再 aiImageQualityToLabel 转中文
  quality: aiImageLabelToQuality(source.quality || "LOW"),
  count: String(source.imageCount || 1) + "张",
  model: aiImageModelToLabel(source.model),
});

const pad = (n: number) => String(n).padStart(2, "0");

const formatWorkTime = (value?: string): string => {
  if (!value) return "";
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toTemplateDetail = (template: AiImageTemplate): DetailItem => ({
  title: template.title || emptyItem.title,
  image: template.coverImageUrl || emptyItem.image,
  description: template.description || "",
  tags: parseAiImageTags(template.tags),
  prompt: template.prompt || "",
  params: toParams(template),
  referenceImageUrl: (template as { referenceImageUrl?: string | null }).referenceImageUrl ?? null,
});

const toWorkDetail = (task: AiImageTask, imageIndex = 0): DetailItem => ({
  title: String(task.prompt || "未命名作品").replace(/\s+/g, " ").trim().slice(0, 22),
  image: task.imageUrls?.[Math.max(0, imageIndex)] || task.imageUrls?.[0] || "",
  description: "",
  tags: ["AI 原创", aiImageQualityToLabel(task.quality), aiImageModelToLabel(task.model)],
  prompt: task.prompt || "",
  params: toParams(task),
  createdAt: formatWorkTime(task.createTime),
  referenceImageUrl: task.referenceImageUrl ?? null,
});

const icon = (name: string) => {
  const props = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7 };
  switch (name) {
    case "back": return <svg {...props}><path d="m15 18-6-6 6-6" /></svg>;
    case "copy": return <svg {...props}><rect x="8" y="8" width="11" height="11" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></svg>;
    case "sparkles": return <svg {...props}><path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2ZM6 14l.8 2.2L9 17l-2.2.8L6 20l-.8-2.2L3 17l2.2-.8Z" /></svg>;
    case "model": return <svg {...props}><rect x="6" y="6" width="12" height="12" rx="2" /><path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M18 9h4M2 15h4M18 15h4" /></svg>;
    case "ratio": return <svg {...props}><rect x="4" y="3" width="16" height="18" rx="2" /></svg>;
    case "resolution": return <svg {...props}><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" /></svg>;
    case "quality": return <svg {...props}><path d="m12 3 1.4 4.1L18 8.5l-4.6 1.4L12 14l-1.4-4.1L6 8.5l4.6-1.4ZM6 15l.8 2.2L9 18l-2.2.8L6 21l-.8-2.2L3 18l2.2-.8Z" /></svg>;
    case "count": return <svg {...props}><path d="m12 3 9 5-9 5-9-5ZM3 12l9 5 9-5M3 16l9 5 9-5" /></svg>;
    case "download": return <svg {...props}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>;
    case "more": return <svg {...props}><circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" /></svg>;
    case "trash": return <svg {...props}><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" /></svg>;
    case "clock": return <svg {...props}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
    default: return <svg {...props}><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4M11 8v6M8 11h6" /></svg>;
  }
};

export const AiImageDetail: FC = () => {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const workId = query.get("work");
  const templateId = query.get("template");
  const imageIndex = Math.max(0, Number(query.get("imageIndex")) || 0);
  const [item, setItem] = useState<DetailItem>(emptyItem);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [estimatedPoint, setEstimatedPoint] = useState<number | null>(null);
  const [pointLoading, setPointLoading] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const moreWrapRef = useRef<HTMLDivElement | null>(null);
  const isWork = Boolean(workId);
  const numericWorkId = Number(workId);

  useEffect(() => {
    let active = true;
    const id = Number(isWork ? workId : templateId);
    const load = async () => {
      setLoading(true);
      try {
        const next = isWork && Number.isFinite(id)
          ? toWorkDetail(await getAiImageTaskApi(id), imageIndex)
          : Number.isFinite(id)
            ? toTemplateDetail(await getAiImageTemplateApi(id))
            : emptyItem;
        if (active) setItem(next);
      } catch (error) {
        if (active) setToast(error instanceof Error ? error.message : "详情加载失败");
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [imageIndex, isWork, templateId, workId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  // 点外面关闭「更多」菜单
  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (moreWrapRef.current && !moreWrapRef.current.contains(event.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [moreOpen]);

  /** 按当前分辨率 / 画质 / 数量调用 POST /ai-image/point-preview 拿预计消耗积分 */
  useEffect(() => {
    if (isWork) {
      // 作品详情显示「本次实际消耗」—— 等后端下发 task 时再补字段；现在按接口约定为 0 占位
      setEstimatedPoint(null);
      return;
    }
    const resolution = item.params.resolution;
    const quality = item.params.quality;
    const imageCount = Number(String(item.params.count).replace(/\D/g, "")) || 1;
    if (!resolution || !quality) return;
    setPointLoading(true);
    void previewAiImagePointApi({
      resolution,
      quality: quality as "LOW" | "HIGH" | "EXTREME",
      imageCount,
    })
      .then((res) => setEstimatedPoint(res?.estimatedPoint ?? null))
      .catch(() => setEstimatedPoint(null))
      .finally(() => setPointLoading(false));
  }, [isWork, item.params.resolution, item.params.quality, item.params.count]);

  const back = useCallback(() => navigate("/hub/ai-image" + (isWork ? "?tab=works" : "")), [isWork, navigate]);
  const copy = useCallback(async () => {
    try { await navigator.clipboard.writeText(item.prompt); }
    catch {
      const input = document.createElement("textarea");
      input.value = item.prompt;
      document.body.append(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    setToast("完整提示词已复制");
  }, [item.prompt]);

  const useTemplate = useCallback(async () => {
    const id = Number(isWork ? workId : templateId);
    try {
      const latest = isWork && Number.isFinite(id)
        ? toWorkDetail(await getAiImageTaskApi(id))
        : Number.isFinite(id)
          ? toTemplateDetail(await getAiImageTemplateApi(id))
          : item;
      localStorage.setItem("ai_image_template_apply", JSON.stringify({
        id: isWork ? workId : templateId,
        title: latest.title,
        prompt: latest.prompt,
        params: latest.params,
        // 参考图 URL：模板 / 作品原图带过来的「参考图」地址，带回工作台后会作为生图的参考图使用
        referenceImageUrl: latest.referenceImageUrl ?? "",
      }));
      navigate("/hub/ai-image?" + (isWork ? "fromWork=1" : "fromTemplate=1"));
    } catch (error) {
      setToast(error instanceof Error && error.message ? error.message : "配置加载失败");
    }
  }, [isWork, item, navigate, templateId, workId]);

  const exportImage = useCallback(() => {
    if (!item.image) return;
    const safeTitle = String(item.title || "AI生图作品").replace(/[\\/:*?"<>|]/g, "-");
    downloadFileSmart(item.image, `${safeTitle}.jpg`);
    setToast("作品导出已开始");
    setMoreOpen(false);
  }, [item.image, item.title]);

  const handleDelete = useCallback(async () => {
    setMoreOpen(false);
    if (!isWork || !Number.isFinite(numericWorkId)) return;
    if (deleting) return;
    if (!window.confirm(`确定删除「${item.title || "AI 生图作品"}」吗？删除后不可恢复。`)) return;
    setDeleting(true);
    try {
      const ok = await deleteAiImageTaskApi(numericWorkId);
      if (!ok) {
        setToast("删除失败，请稍后重试");
        return;
      }
      setToast("作品已删除",);
      window.setTimeout(() => navigate("/hub/ai-image?tab=works"), 350);
    } catch (error) {
      setToast(error instanceof Error && error.message ? error.message : "删除失败");
    } finally {
      setDeleting(false);
    }
  }, [deleting, isWork, item.title, navigate, numericWorkId]);

  const paramEntries = useMemo(
    () => (["ratio", "resolution", "quality", "count", "model"] as Key[]).map((key) => ({
      key,
      label: labels[key],
      value: key === "quality" ? aiImageQualityToLabel(item.params[key]) : item.params[key],
    })),
    [item.params],
  );

  const generatedTime = item.createdAt;

  return (
    <div className="ai-image-detail-page">
      <div className="detail-scroll">
        <div className="detail-shell">
          <header className="detail-toolbar">
            <button className="detail-back" type="button" onClick={back} aria-label={isWork ? "返回我的作品" : "返回模板列表"} title={isWork ? "返回我的作品" : "返回模板列表"}>
              {icon("back")}
            </button>
            {isWork ? (
              <div className="detail-work-tools">
                <button className="toolbar-export" type="button" onClick={exportImage} disabled={!item.image}>
                  {icon("download")}<span>导出</span>
                </button>
                <div className="detail-more-wrap" ref={moreWrapRef}>
                  <button
                    className="detail-more-button"
                    type="button"
                    aria-label="更多作品操作"
                    aria-expanded={moreOpen}
                    onClick={() => setMoreOpen((open) => !open)}
                  >
                    {icon("more")}
                  </button>
                  {moreOpen ? (
                    <div className="detail-more-menu">
                      <button className="detail-delete-button" type="button" onClick={handleDelete} disabled={deleting}>
                        {icon("trash")}<span>{deleting ? "删除中…" : "删除作品"}</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </header>

          <section className="detail-hero">
            <h1>{item.title}</h1>
            {generatedTime ? (
              <p className="generated-time">
                {icon("clock")}<span>生成于 {generatedTime}</span>
              </p>
            ) : null}
          </section>

          <section className="preview-card" onClick={() => item.image && setPreviewOpen(true)} role={item.image ? "button" : undefined}>
            <div className="image-stage">
              {item.image
                ? <img src={item.image} alt={item.title} />
                : <span className="image-placeholder">{loading ? "Loading" : "暂无图片"}</span>}
            </div>
          </section>

          <div className="detail-content">
            <section className="detail-section">
              <div className="section-head section-head--inline">
                <div><h2>完整提示词</h2><span>Prompt</span></div>
                <button className="copy-button" type="button" onClick={copy}>{icon("copy")}复制</button>
              </div>
              <div className="prompt-box">{item.prompt}</div>
            </section>

            <section className="detail-section">
              <div className="section-head"><div><h2>生成参数</h2><span>Generation settings</span></div></div>
              <div className="params-grid">
                {paramEntries.map(({ key, label, value }) => (
                  <div className="param" key={key}>
                    <span className="param-icon">{icon(key)}</span>
                    <span>
                      <span className="param-label">{label}</span>
                      <span className="param-value" title={value}>{value}</span>
                    </span>
                  </div>
                ))}
              </div>
              {!isWork ? (
                <div className="cost-card">
                  <span>按此参数生成，单次预计消耗</span>
                  <strong>
                    {pointLoading
                      ? "预估中…"
                      : estimatedPoint !== null
                        ? `${estimatedPoint} 积分`
                        : "—"}
                  </strong>
                </div>
              ) : null}
            </section>

            {item.referenceImageUrl ? (
              <section className="detail-section">
                <div className="section-head"><div><h2>参考图片</h2><span>Reference</span></div></div>
                <div className="reference-image-frame">
                  <img src={item.referenceImageUrl} alt="参考图片" />
                </div>
              </section>
            ) : null}

            <footer className="action-area">
              <p className="action-note">
                {isWork
                  ? "再次编辑会把这张作品的提示词与参数带回工作台，原作品仍会保留在列表中。"
                  : "进入生成工作台后，仍可继续调整提示词、画面比例、模型和生成数量。"}
              </p>
              <button className="primary-button" type="button" onClick={useTemplate}>
                {icon("sparkles")}<span>{isWork ? "再次编辑" : "做同款"}</span>
              </button>
            </footer>
          </div>
        </div>
      </div>
      <AppToast config={toast ? { type: "success", message: toast, closable: false } : null} onClose={() => setToast("")} />
      {previewOpen && item.image ? (
        <div className="preview-modal" role="dialog" aria-modal="true" onClick={() => setPreviewOpen(false)}>
          <button type="button" aria-label="close">×</button>
          <img src={item.image} alt={item.title} onClick={(event) => event.stopPropagation()} />
        </div>
      ) : null}
    </div>
  );
};